<template>
	<div class="stretch-layout">
		<card xs-tile sm-tile>
			<card-text>
				<div class="flex justify-between items-center">
					<div class="flex flex-column">
						<form-rating readonly left :value.number="eventStore.event.rating.avg"/>
						<span class="text-xs leading-tight mt-4 ml-4 text-text-color">{{
								$t('param_label.ratings', eventStore.event.rating.count, {'count': eventStore.event.rating.count})
							}} <span
								v-if="eventStore.event.rating.avg">({{ $n(eventStore.event.rating.avg, {minimumFractionDigits: 1, maximumFractionDigits: 1}) }})</span></span>
					</div>
					<btn v-if="eventStore.isParticipant" :to="$web('events.ratings.create', eventStore.event._key)" :color="eventStore.colorStyle ?? 'primary'">
						<icon :icon="icons.create_rating"/>
						<span>{{ $t('action.event_rating.create') }}</span>
					</btn>
				</div>
			</card-text>
		</card>
		<card xs-tile sm-tile class="flex-grow-1">
			<data-loader :url="$api('events.ratings.index', eventStore.event.id)"
						 as-guest
						 on-scroll
						 :params="params"
						 v-model:data="ratings">

				<template #loading>
					<template v-if="eventStore.event.rating.count">
						<event-rating-skeleton v-for="ratingLoader in Math.min(params.limit, eventStore.event.rating.count)" :key="ratingLoader" divided/>
					</template>
					<missing-info v-else no-spacing>{{ $t('missing.rating.index') }}</missing-info>
				</template>

				<template v-slot="{data}">
					<event-rating v-for="rating in data" :key="rating.id"
								  :rating="rating"
								  @edit="editRating(rating)"
								  @show-comments="showComments(rating)"
								  :editable="$auth.id && rating.user_id === $auth.id"
								  divided/>
				</template>

				<template #missing>
					<missing-info no-spacing xs-tile sm-tile class="flex-grow-1">
						{{ $t('missing.rating.index') }}
					</missing-info>
				</template>
			</data-loader>
		</card>

		<lazy-ajax-form-modal v-if="$auth.loggedIn && selectedRating" v-model="showEditRatingModal"
							  :form-options="{action: $api('events.ratings.update', [eventStore.event.id, selectedRating.id]), data: editRatingData, method: 'PUT'}"
							  :modal-options="{title: $t('action.edit')}"
							  @success="handleUpdatedRating">

			<lazy-event-rating-form-inputs :data="editRatingData" edit/>

			<template #footer>
				<lazy-delete-model-btn :action="$api('events.ratings.destroy', [eventStore.event.id, selectedRating.id])"
									   :model="selectedRating"
									   @success="handleDeletedRating(selectedRating)"
									   outlined>
				</lazy-delete-model-btn>
			</template>

		</lazy-ajax-form-modal>

		<slide-sheet v-model="showCommentsModal" :title="$t('label.comments')" :width="480">
			<template v-if="selectedRating" #default>
				<comment-feed :url="`/v1/events/${eventStore.event.id}/ratings/${selectedRating.id}/comments`"
							  v-model:comments-count="commentsCount"
							  v-model:comments="comments"
							  :cache-buster="commentLoaderCacheBuster" class="px-16"/>
			</template>

			<template v-if="selectedRating" #footer>
				<div v-if="$auth.loggedIn" class="py-16 pr-16 pl-12 border-t">
					<lazy-comment-form :action="`/v1/events/${eventStore.event.id}/ratings/${selectedRating.id}/comments`"
									   :inline="!$device.isDesktop"
									   @success="handleCreatedComment"/>
				</div>
				<lazy-login-to-see-more v-else flat no-spacing :text="$t('action.login_to_comment')"/>
			</template>

		</slide-sheet>
	</div>
</template>

<script setup lang="ts">
import {EventAttendanceMode, type EventRating} from "@spoferan/spoferan-ts-core";
import {EventPageType} from "@spoferan/spoferan-ts-core";
import {mdiSquareEditOutline} from "@mdi/js";
import {useEventStore} from "../../../../store/event";

const eventStore = useEventStore();
const {t} = useI18n();
const {$api, $web, $auth, $date, $image} = useNuxtApp();
const route = useRoute();

// Make sure the page is activated
if (!eventStore.hasActivePage(EventPageType.Ratings)) {
	await navigateTo($web('events.show', route.params.event));
}

const icons = {
	create_rating: mdiSquareEditOutline
};

const params = {
	limit: 10,
	relations: ['user'],
	with_permissions: true
};

const ratings = ref([]);
const selectedRating = ref(null);
const showEditRatingModal = ref(false);
const editRatingData = ref({});
const showCommentsModal = ref(false);
const commentLoaderCacheBuster = ref(null);

const {comments, commentsCount, handleCreatedComment} = useCommentable();
watch(commentsCount, (newValue) => {
	if (selectedRating.value) {
		selectedRating.value.comments_count = newValue;
	}
});

function editRating(rating: EventRating) {
	selectedRating.value = rating;
	editRatingData.value = {
		rating: rating.rating,
		description: rating.description,
		private: rating.private,
	};
	showEditRatingModal.value = true;
}

function handleDeletedRating(rating) {
	const index = ratings.value.indexOf(rating);
	if (index > -1) {
		ratings.value.splice(index, 1);
	}
	showEditRatingModal.value = false;
}

function handleUpdatedRating(rating) {
	const existingRating = ratings.value.find(existingRating => existingRating.id === rating.id);
	if (existingRating) {
		existingRating.rating = rating.rating;
		existingRating.description = rating.description;
		existingRating.private = rating.private;
	}
}

function showComments(rating) {
	selectedRating.value = rating;
	commentsCount.value = rating.comments_count;

	// We do not inline the params in the template as it would be reactive and therefore trigger a reload when we call 'updateActivityCacheBuster'
	commentLoaderCacheBuster.value = $date(rating.updated_at).unix();

	nextTick(() => {
		showCommentsModal.value = true;
	});
}

useSchemaOrg([
	defineEvent({
		"@type": "SportsEvent",
		"name": eventStore.event.display_name,
		"startDate": eventStore.event.start_date,
		"endDate": eventStore.event.end_date,
		'location': {
			'@type': 'Place',
			'name': eventStore.event.location_name,
			'address': {
				'@type': 'PostalAddress',
				'addressLocality': eventStore.event.city,
				'addressRegion': eventStore.event.state_code?.toUpperCase(),
				'addressCountry': eventStore.event.country_code?.toUpperCase(),
				'postalCode': eventStore.event.postcode,
				'streetAddress': eventStore.event.street
			}
		},
		"image": $image.format(eventStore.event.avatar_thumbnail),
		"eventAttendanceMode": eventStore.event.attendance_mode === EventAttendanceMode.Attendance ? 'OfflineEventAttendanceMode' : (eventStore.event.attendance_mode === EventAttendanceMode.Virtual ? 'OnlineEventAttendanceMode' : 'MixedEventAttendanceMode'),
	})
])

useMeta({
	title: eventStore.buildPageTitle(t('view.events.ratings.index.title', {event: eventStore.event.display_name})),
	description: t('view.events.ratings.index.description', {event: eventStore.event.display_name})
})

</script>

